import React from "react";
import { Phone, MapPin, Clock, Navigation } from "lucide-react";
import { Button } from "./ui/button";

const MAPS_EMBED =
  "https://www.google.com/maps?q=C.+Melilla+5+Leganés+Madrid&output=embed";
const MAPS_DIRECTIONS =
  "https://www.google.com/maps/dir/?api=1&destination=C.+Melilla+5,+Local+3,+28913+Leganés,+Madrid";

const schedule = [
  { day: "Lunes", hours: "9:30 – 20:00" },
  { day: "Martes", hours: "9:30 – 20:00" },
  { day: "Miércoles", hours: "9:30 – 20:00" },
  { day: "Jueves", hours: "9:30 – 20:00" },
  { day: "Viernes", hours: "9:30 – 20:00" },
  { day: "Sábado", hours: "10:00 – 13:00" },
  { day: "Domingo", hours: "Cerrado", closed: true },
];

export const ContactCTA = () => {
  return (
    <section
      id="ubicacion"
      data-testid="contact-section"
      className="py-20 md:py-28 bg-slate-50/60"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-800 leading-tight">
            ¿Tu mascota necesita una revisión?{" "}
            <span className="text-violet-600">Te esperamos.</span>
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Pide cita por teléfono y te atenderemos con el cariño y la atención
            que tu mascota merece.
          </p>
          <a
            href="tel:684366280"
            data-testid="footer-call-button"
            className="mt-6 inline-block"
          >
            <Button
              size="lg"
              className="rounded-full bg-violet-600 hover:bg-violet-700 text-white h-14 px-8 text-base font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              <Phone className="w-5 h-5 mr-2" strokeWidth={2.4} />
              Llamar al 684 36 62 80
            </Button>
          </a>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-8 lg:gap-10">
          {/* Left: Info */}
          <div className="bg-white rounded-3xl p-8 md:p-10 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <h3 className="font-['Outfit'] text-xl font-semibold text-slate-800 mb-6">
              Horarios y contacto
            </h3>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center ring-1 ring-violet-100">
                  <MapPin className="w-5 h-5" strokeWidth={2.2} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                    Dirección
                  </p>
                  <p
                    className="mt-1 text-slate-800 font-medium"
                    data-testid="contact-address"
                  >
                    C. Melilla, 5, Local 3, 28913 Leganés, Madrid
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center ring-1 ring-violet-100">
                  <Phone className="w-5 h-5" strokeWidth={2.2} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                    Teléfono
                  </p>
                  <a
                    href="tel:684366280"
                    data-testid="contact-phone"
                    className="mt-1 block text-slate-800 font-medium hover:text-violet-600 transition-colors"
                  >
                    684 36 62 80
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center ring-1 ring-violet-100">
                  <Clock className="w-5 h-5" strokeWidth={2.2} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                    Horario
                  </p>
                  <ul
                    data-testid="contact-schedule"
                    className="mt-2 divide-y divide-slate-100"
                  >
                    {schedule.map((s) => (
                      <li
                        key={s.day}
                        className="flex items-center justify-between py-2 text-sm"
                      >
                        <span className="text-slate-700 font-medium">
                          {s.day}
                        </span>
                        <span
                          className={
                            s.closed
                              ? "text-slate-400 font-medium"
                              : "text-slate-800 font-semibold tabular-nums"
                          }
                        >
                          {s.hours}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <a
              href={MAPS_DIRECTIONS}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="directions-button"
              className="mt-8 inline-block"
            >
              <Button
                size="lg"
                variant="outline"
                className="rounded-full h-12 px-6 border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800 font-semibold"
              >
                <Navigation className="w-4 h-4 mr-2" strokeWidth={2.4} />
                Cómo llegar
              </Button>
            </a>
          </div>

          {/* Right: Map */}
          <div className="rounded-3xl overflow-hidden border border-slate-100 shadow-[0_20px_50px_rgb(0,0,0,0.08)] min-h-[420px] bg-slate-100 relative">
            <iframe
              data-testid="google-map-iframe"
              title="Ubicación Norivet — C. Melilla 5, Leganés"
              src={MAPS_EMBED}
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: "420px" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
